import React, { useMemo } from "react";
import { Mafs, Coordinates, Point, Line, Text, Theme, Vector } from "mafs";

export const NumberLine = ({ config = {}, variables = {} }) => {
    const {
        min = -10.0,
        max = 10.0,
        tickInterval = 1.0,
        elements: rawElements = [],
    } = config;

    // Auto-fix inverted bounds (matches NumberLineConfig constructor)
    const fixedMin = Math.min(Number(min), Number(max));
    const fixedMax = Math.max(Number(min), Number(max));

    const safeTick = (() => {
        const t = Number(tickInterval);
        return Number.isFinite(t) && t > 0 ? t : 1;
    })();

    // Resolve either:
    // - a variable reference by key name (string matches variables[key])
    // - a numeric string ("2.5")
    // - a number
    const resolve = (val, fallback = undefined) => {
        if (val === undefined || val === null) return fallback;

        if (typeof val === "string") {
            if (Object.prototype.hasOwnProperty.call(variables, val)) {
                const v = Number(variables[val]);
                return Number.isFinite(v) ? v : fallback;
            }
            const n = Number(val);
            return Number.isFinite(n) ? n : fallback;
        }

        const n = Number(val);
        return Number.isFinite(n) ? n : fallback;
    };

    const normType = (t) => String(t || "").toUpperCase();      // "point" -> "POINT"
    const normDir = (d) => String(d || "RIGHT").toUpperCase();  // "left" -> "LEFT"

    const elements = useMemo(() => {
        return (rawElements || []).map((el, i) => {
            const type = normType(el.type); // IMPORTANT: matches Java @JsonValue
            const direction = normDir(el.direction);
            const magnitude = resolve(el.magnitude, 3); // matches your default

            const value = resolve(el.value);
            const start = resolve(el.start);
            const end = resolve(el.end);

            // Closed/open endpoints: Java uses primitive boolean -> defaults false if missing.
            // For typical number-line UX, treat "missing" as closed in UI by using ?? true.
            // But because Jackson will send false when missing, we can only safely do:
            // - if property exists, use it
            // - else default to true
            const hasStartClosed = Object.prototype.hasOwnProperty.call(el, "startClosed");
            const hasEndClosed = Object.prototype.hasOwnProperty.call(el, "endClosed");

            const startClosed = hasStartClosed ? Boolean(el.startClosed) : true;
            const endClosed = hasEndClosed ? Boolean(el.endClosed) : true;

            // Colors: accept explicit CSS string or default theme
            const color = el.color || Theme.blue;

            return {
                ...el,
                key: el.id ?? i,
                type,
                direction,
                magnitude,
                value,
                start,
                end,
                startClosed,
                endClosed,
                color,
            };
        });
    }, [rawElements, variables]);

    // Tick label formatter (avoid -0 and long floats)
    const formatTick = (n) => {
        const r = Number(n.toFixed(6));
        return Object.is(r, -0) ? "0" : String(r);
    };

    return (
        <Mafs viewBox={{ x: [fixedMin, fixedMax], y: [-1.2, 1.2] }} pan={false} height={160}>
            <Coordinates.Cartesian
                yAxis={{ lines: false, labels: false }}
                xAxis={{
                    lines: safeTick,         // actual tick spacing
                    subdivisions: false,
                    labels: (n) => formatTick(n),
                }}
            />

            {elements.map((el) => {
                const isHollowStart = !el.startClosed;
                const isHollowEnd = !el.endClosed;

                // ---------- POINT ----------
                if (el.type === "POINT") {
                    const x = el.value;
                    if (x === undefined) return null;

                    return (
                        <g key={el.key}>
                            <Point x={x} y={0} color={el.color} />
                            {el.label && (
                                <Text x={x} y={0.4} size={16} attach="s">
                                    {el.label}
                                </Text>
                            )}
                        </g>
                    );
                }

                // ---------- VECTOR ----------
                if (el.type === "VECTOR") {
                    if (el.start === undefined) return null;

                    // If end missing, compute from magnitude + direction (matches validator intent)
                    const isLeft = el.direction === "LEFT";
                    const end = el.end !== undefined ? el.end : (isLeft ? el.start - el.magnitude : el.start + el.magnitude);

                    return (
                        <g key={el.key}>
                            <Vector tail={[el.start, 0.3]} tip={[end, 0.3]} color={el.color} weight={3} />
                            {el.label && (
                                <Text x={(el.start + end) / 2} y={0.6} size={14}>
                                    {el.label}
                                </Text>
                            )}
                        </g>
                    );
                }

                // ---------- INTERVAL (or RAY encoded as interval with one missing endpoint) ----------
                if (el.type === "INTERVAL") {
                    const hasStart = el.start !== undefined;
                    const hasEnd = el.end !== undefined;

                    // RAY case: one end missing
                    if (hasStart ^ hasEnd) {
                        const isLeft = el.direction === "LEFT";

                        // Anchor point is the provided endpoint (start or end)
                        const anchor = hasStart ? el.start : el.end;

                        // Compute the "arrow end" from anchor ± magnitude
                        const arrowEnd = isLeft ? anchor - el.magnitude : anchor + el.magnitude;

                        // Segment goes from anchor to arrowEnd (order doesn't matter for Line.Segment)
                        const segA = hasStart ? anchor : arrowEnd; // if anchor is end (x<k), arrow on left
                        const segB = hasStart ? arrowEnd : anchor;

                        // Arrow tip is at arrowEnd
                        const arrowTailX = arrowEnd + (isLeft ? 0.6 : -0.6);

                        // Endpoint marker is at anchor (closed/open depends on which endpoint it is)
                        const endpointIsHollow = hasStart ? isHollowStart : isHollowEnd;

                        return (
                            <g key={el.key}>
                                <Line.Segment point1={[segA, 0]} point2={[segB, 0]} color={el.color} weight={4} />
                                <Vector tail={[arrowTailX, 0]} tip={[arrowEnd, 0]} color={el.color} weight={3} />

                                <Point
                                    x={anchor}
                                    y={0}
                                    color={endpointIsHollow ? "white" : el.color}
                                    svgCircleProps={
                                        endpointIsHollow ? { stroke: el.color, strokeWidth: 2 } : undefined
                                    }
                                />

                                {el.label && (
                                    <Text x={(segA + segB) / 2} y={0.5} size={14}>
                                        {el.label}
                                    </Text>
                                )}
                            </g>
                        );
                    }

                    // Normal interval: both endpoints required
                    if (!hasStart || !hasEnd) return null;

                    // Auto-fix reversed start/end
                    const a = Math.min(el.start, el.end);
                    const b = Math.max(el.start, el.end);

                    return (
                        <g key={el.key}>
                            <Line.Segment point1={[a, 0]} point2={[b, 0]} color={el.color} weight={4} />

                            <Point
                                x={a}
                                y={0}
                                color={isHollowStart ? "white" : el.color}
                                svgCircleProps={isHollowStart ? { stroke: el.color, strokeWidth: 2 } : undefined}
                            />
                            <Point
                                x={b}
                                y={0}
                                color={isHollowEnd ? "white" : el.color}
                                svgCircleProps={isHollowEnd ? { stroke: el.color, strokeWidth: 2 } : undefined}
                            />

                            {el.label && <Text x={(a + b) / 2} y={0.5} size={14}>{el.label}</Text>}
                        </g>
                    );
                }

                return null;
            })}
        </Mafs>
    );
};
